import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { EditorState } from "draft-js";
import { AvField, AvForm } from "availity-reactstrap-validation";
import AvFieldSelect from "components/Common/AvFieldSelect";
import html2pdf from "html2pdf.js";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
} from "reactstrap";
import {
  showSuccessNotification,
  showErrorNotification,
} from "store/notifications/actions";
import { sendUserEmail } from "apis/users";
import useModal from "hooks/useModal";
import Loader from "components/Common/TableLoader";
import { addUserTemplate, getUserTemplates } from "apis/systemEmails";
import {
  toBase64,
  getBase64ContentType,
  removeBase64Metadata,
} from "helpers/base64";
import { languageOptions, fields } from "constants/userEmails";

function sendEmailInvestor({ clientData, isLead, t }) {
  const dispatch = useDispatch();
  const [show, toggle] = useModal();
  const [loading, setLoading] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [useTemplate, setUseTemplate] = useState(false);
  const [subject, setSubject] = useState();
  const [attachments, setAttachments] = useState();
  const [templateTitle, setTemplateTitle] = useState();
  const [language, setLanguage] = useState(languageOptions[0].value);
  const [emails, setEmails] = useState([]);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const { currentProvider, sendGrid, smtp } =
    useSelector((state) => state.Profile.emails) || {};
  const [data, setData] = useState("");

  const addTemplate = () => {
    setLoadingTemplate(true);
    const content = {};
    languageOptions.forEach((lang) => {
      if (lang.value === language) {
        content[lang.value] = {
          subject,
          body: data,
        };
      } else {
        content[lang.value] = {
          subject: "",
          body: "",
        };
      }
    });
    addUserTemplate({
      title: templateTitle,
      content,
      fields,
    })
      .then(() => {
        dispatch(showSuccessNotification("Template saved successfully"));
        setLoadingTemplate(false);
      })
      .catch(() => {
        dispatch(showErrorNotification("Error while saving template"));
        setLoadingTemplate(false);
      });
  };

  useEffect(() => {
    if (currentProvider === "sendGrid") {
      setEmails([sendGrid]);
    } else if (currentProvider === "smtp") {
      setEmails([smtp]);
    }
  }, [currentProvider, sendGrid, smtp]);

  const generatePDFBlob = async (elementId = "profile-investors-table") => {
    const element = document.getElementById(elementId);
    if (!element) return null;

    return new Promise((resolve, reject) => {
      const opt = {
        margin: 0.3,
        filename: "investors-report.pdf",
        image: { 
          type: "jpeg", 
          quality: 0.98 
        },
        html2canvas: { 
          scale: 2 
        },
        jsPDF: { 
          unit: "in", 
          format: "a4", 
          orientation: "landscape" 
        },
      };

      html2pdf()
        .set(opt)
        .from(element)
        .outputPdf("blob")
        .then((blob) => {
          const file = new File([blob], "investors-report.pdf", {
            type: "application/pdf",
          });
          resolve(file);
        })
        .catch(reject);
    });
  };

  return (
    <React.Fragment>
      <button
        type="button"
        disabled={isLead}
        className="btn btn-primary waves-effect waves-light w-100 me-1"
        onClick={toggle}
      >
        {t("Send Email")}
      </button>
      <Modal isOpen={show} toggle={toggle} centered={true}>
        <ModalHeader toggle={toggle}>{t("Send Email ")}</ModalHeader>
        <ModalBody>
          <AvForm
            onValidSubmit={async (e, v) => {
              setLoading(true);
              const attachmentsArray = [];
              const pdfFile = await generatePDFBlob("profile-investors-table");
              if (pdfFile) {
                const base64 = await toBase64(pdfFile);
                attachmentsArray.push({
                  content: removeBase64Metadata(base64),
                  type: getBase64ContentType(base64),
                  filename: pdfFile.name,
                  disposition: "attachment",
                  encoding: "base64"
                });
              }
              sendUserEmail({
                from: v.from,
                to: clientData.email,
                subject: subject,
                fields,
                provider: currentProvider,
                clientData: {
                  firstName: clientData.firstName,
                  lastName: clientData.lastName,
                  email: clientData.email,
                },
                body: data,
                attachments: attachmentsArray,
              })
                .then(() => {
                  toggle();
                  dispatch(showSuccessNotification("Email sent successfully"));
                  setSubject("");
                  setLanguage(languageOptions[0].value);
                  setData("");
                  setLoading(false);
                })
                .catch(() => {
                  dispatch(showErrorNotification("Error while sending email"));
                  setLoading(false);
                });
            }}
          >
            <AvFieldSelect
              name="from"
              label={t("From")}
              options={
                emails.length > 0 && emails[0]?.fromEmail
                  ? emails.map((email) => {
                    return {
                      value: email,
                      label: email?.fromEmail,
                    };
                  })
                  : ""
              }
              required
            />
            {!emails[0]?.fromEmail && (
              <small className="d-flex justify-content-end">
                {t("Don't have an email?")}&nbsp;
                <Link to="/profile" className="fw-bold">
                  {t("Link one")}
                </Link>
              </small>
            )}
            <AvField
              name="to"
              label={t("To")}
              value={clientData.email}
              disabled={true}
            />
            <AvFieldSelect
              name="language"
              label={t("Language")}
              options={languageOptions}
              value={language}
              onChange={(e) => setLanguage(e)}
              required
            />
            <AvField
              name="subject"
              label={t("Subject")}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
            />
            <ModalFooter className="d-flex justify-content-center pb-0">
              {loadingTemplate ? (
                <Loader />
              ) : (
                useTemplate && (
                  <Button
                    type="button"
                    color="primary"
                    className="me-2"
                    onClick={addTemplate}
                  >
                    {t("Save Template")}
                  </Button>
                )
              )}
              {loading ? (
                <Loader />
              ) : (
                <Button type="submit" color="primary">
                  {t("Send")}
                </Button>
              )}
            </ModalFooter>
          </AvForm>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
}

export default withTranslation()(sendEmailInvestor);
